import { logWarn } from '@ptek/client-logger';
import { useEffect, useState } from 'react';
import { useWidgetAuth } from '../context/AuthContext';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../utils/userPreferences';

interface MicrophoneDevice {
  deviceId: string;
  label: string;
  displayName: string;
  type: string;
  description: string;
}

interface UseMicrophoneSettingsReturn {
  availableMicrophones: MicrophoneDevice[];
  selectedMicrophoneId: string;
  micSettingsAnchor: HTMLElement | null;
  microphoneError: string | null;
  isLoadingMicrophones: boolean;
  handleMicSettingsClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleMicSettingsClose: () => void;
  handleMicrophoneSelect: (microphoneId: string) => void;
  getSelectedDeviceId: () => string;
  refreshMicrophones: () => Promise<void>;
}

const LEGACY_STORAGE_KEY = 'ambient-scribe-microphone-preference';

const saveMicrophonePreference = (deviceId: string, userId?: string): void => {
  try {
    saveUserSettingsPreferences(userId, { microphoneDeviceId: deviceId });
  } catch (error) {
    logWarn(
      'useMicrophoneSettings',
      'Failed to save microphone preference:',
      error
    );
  }
};

const loadMicrophonePreference = (userId?: string): string => {
  try {
    const preferences = loadUserSettingsPreferences(userId);
    let deviceId = preferences.microphoneDeviceId;

    if (!deviceId) {
      try {
        const legacyDeviceId = localStorage.getItem(LEGACY_STORAGE_KEY);
        if (legacyDeviceId) {
          saveMicrophonePreference(legacyDeviceId, userId);
          localStorage.removeItem(LEGACY_STORAGE_KEY);
          deviceId = legacyDeviceId;
        }
      } catch (legacyError) {
        logWarn(
          'useMicrophoneSettings',
          'Failed to migrate legacy microphone preference:',
          legacyError
        );
      }
    }

    return deviceId || 'default';
  } catch (error) {
    logWarn(
      'useMicrophoneSettings',
      'Failed to load microphone preference:',
      error
    );
    return 'default';
  }
};

const getMicrophoneDisplayName = (device: MediaDeviceInfo): string => {
  if (!device.label) {
    return `Microphone ${device.deviceId.slice(0, 8)}...`;
  }

  let displayName = device.label;

  const cleanupPatterns = [
    /^Default - /i,
    /^External /i,
    / - Default$/i,
    / \(.*?\)$/,
    / Microphone$/i,
    / Audio$/i,
    / Input$/i,
  ];

  cleanupPatterns.forEach((pattern) => {
    displayName = displayName.replace(pattern, '');
  });

  return displayName.trim() || device.label;
};

const getMicrophoneDetails = (device: MediaDeviceInfo) => {
  const label = device.label.toLowerCase();
  const name = getMicrophoneDisplayName(device);

  let type = 'External';
  let description = 'Connected microphone';

  if (label.includes('built-in') || label.includes('internal')) {
    type = 'Built-in';
    description = 'Internal device microphone';
  } else if (label.includes('usb') || label.includes('usb audio')) {
    type = 'USB';
    description = 'USB connected microphone';
  } else if (label.includes('bluetooth') || label.includes('bt')) {
    type = 'Bluetooth';
    description = 'Wireless bluetooth microphone';
  } else if (
    label.includes('airpods') ||
    label.includes('headphones') ||
    label.includes('headset')
  ) {
    type = 'Headset';
    description = 'Headphones or headset microphone';
  } else if (label.includes('webcam') || label.includes('camera')) {
    type = 'Camera';
    description = 'Camera built-in microphone';
  } else if (label.includes('analog') || label.includes('line')) {
    type = 'Analog';
    description = 'Analog audio input';
  }

  return { name, type, description };
};

const isVirtualMicrophone = (device: MediaDeviceInfo): boolean => {
  const label = device.label.toLowerCase();
  const virtualKeywords = [
    'virtual',
    'obs',
    'voicemeeter',
    'soundflower',
    'loopback',
    'blackhole',
    'vb-audio',
    'cable',
    'aggregate',
    'multi-output',
    'zoom',
    'teams',
    'discord',
    'skype',
    'webex',
    'meet',
    'hangouts',
    'facetime',
    'screen sharing',
    'system audio',
    'stereo mix',
    'what u hear',
    'wave link',
    'elgato',
    'streamlabs',
    'reaper',
    'logic',
    'pro tools',
    'ableton',
    'cubase',
    'fl studio',
    'garage band',
  ];

  return virtualKeywords.some((keyword) => label.includes(keyword));
};

const testDeviceAccessibility = async (deviceId: string): Promise<boolean> => {
  try {
    const constraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
    };

    if (deviceId && deviceId !== 'default') {
      constraints.deviceId = { exact: deviceId };
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: constraints,
    });

    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    logWarn(
      'useMicrophoneSettings',
      `Device ${deviceId} not accessible:`,
      error
    );
    return false;
  }
};

export const useMicrophoneSettings = (): UseMicrophoneSettingsReturn => {
  const { user } = useWidgetAuth();
  const userId = user?.username || user?.email;

  const [availableMicrophones, setAvailableMicrophones] = useState<
    MicrophoneDevice[]
  >([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>(() =>
    loadMicrophonePreference(userId)
  );
  const [micSettingsAnchor, setMicSettingsAnchor] =
    useState<HTMLElement | null>(null);
  const [microphoneError, setMicrophoneError] = useState<string | null>(null);
  const [isLoadingMicrophones, setIsLoadingMicrophones] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  const loadMicrophones = async (): Promise<void> => {
    try {
      setIsLoadingMicrophones(true);
      setMicrophoneError(null);

      if (!hasPermission) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          stream.getTracks().forEach((track) => track.stop());
          setHasPermission(true);
        } catch (permError) {
          logWarn(
            'useMicrophoneSettings',
            'Failed to get microphone permission:',
            permError
          );
          throw permError;
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      const allMicrophones = devices.filter(
        (device) => device.kind === 'audioinput' && !isVirtualMicrophone(device)
      );

      const physicalMicrophones = allMicrophones.filter(
        (device) => device.deviceId !== 'default'
      );
      const defaultDevice = allMicrophones.find(
        (device) => device.deviceId === 'default'
      );

      const microphones =
        physicalMicrophones.length > 0
          ? physicalMicrophones
          : defaultDevice
            ? [defaultDevice]
            : [];

      const microphoneDevices: MicrophoneDevice[] = microphones.map(
        (device) => {
          const details = getMicrophoneDetails(device);
          return {
            deviceId: device.deviceId,
            label: device.label,
            displayName: details.name,
            type: details.type,
            description: details.description,
          };
        }
      );

      setAvailableMicrophones(microphoneDevices);

      const savedDeviceId = loadMicrophonePreference(userId);

      let finalDeviceId = 'default';

      if (microphoneDevices.length > 0) {
        const savedDevice = microphoneDevices.find(
          (device) => device.deviceId === savedDeviceId
        );
        let isValidSavedDevice = false;

        if (savedDevice) {
          const isAccessible = await testDeviceAccessibility(savedDeviceId);
          if (isAccessible) {
            finalDeviceId = savedDeviceId;
            isValidSavedDevice = true;
          } else {
            logWarn(
              'useMicrophoneSettings',
              'Saved device not accessible, falling back'
            );
            isValidSavedDevice = false;
          }
        }

        if (!isValidSavedDevice) {
          const defaultDevice = microphoneDevices.find(
            (device) => device.deviceId === 'default'
          );

          if (defaultDevice) {
            const defaultAccessible = await testDeviceAccessibility('default');
            if (defaultAccessible) {
              finalDeviceId = 'default';
            } else {
              for (const device of microphoneDevices) {
                const isAccessible = await testDeviceAccessibility(
                  device.deviceId
                );
                if (isAccessible) {
                  finalDeviceId = device.deviceId;
                  break;
                }
              }
            }
          } else {
            for (const device of microphoneDevices) {
              const isAccessible = await testDeviceAccessibility(
                device.deviceId
              );
              if (isAccessible) {
                finalDeviceId = device.deviceId;
                break;
              }
            }
          }

          saveMicrophonePreference(finalDeviceId, userId);
        }
      } else {
        finalDeviceId = 'default';
      }

      setSelectedMicrophoneId(finalDeviceId);
    } catch (error) {
      logWarn(
        'useMicrophoneSettings',
        'Failed to enumerate microphones:',
        error
      );

      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setMicrophoneError(
            'Microphone permission denied. Please allow microphone access and refresh.'
          );
        } else if (error.name === 'NotFoundError') {
          setMicrophoneError(
            'No microphones found. Please connect a microphone and refresh.'
          );
        } else {
          setMicrophoneError(`Microphone error: ${error.message}`);
        }
      } else {
        setMicrophoneError(
          'Failed to access microphones. Please check your audio devices.'
        );
      }
    } finally {
      setIsLoadingMicrophones(false);
    }
  };

  useEffect(() => {
    loadMicrophones();

    let deviceChangeTimeout: NodeJS.Timeout | null = null;

    const handleDeviceChange = () => {
      if (deviceChangeTimeout) {
        clearTimeout(deviceChangeTimeout);
      }

      deviceChangeTimeout = setTimeout(() => {
        loadMicrophones();
      }, 1000);
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      if (deviceChangeTimeout) {
        clearTimeout(deviceChangeTimeout);
      }
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      );
    };
  }, []);

  useEffect(() => {
    const savedDeviceId = loadMicrophonePreference(userId);
    setSelectedMicrophoneId(savedDeviceId);
  }, [userId]);

  const handleMicSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMicSettingsAnchor(event.currentTarget);
  };

  const handleMicSettingsClose = () => {
    setMicSettingsAnchor(null);
  };

  const handleMicrophoneSelect = (microphoneId: string) => {
    setSelectedMicrophoneId(microphoneId);
    setMicSettingsAnchor(null);

    saveMicrophonePreference(microphoneId, userId);
  };

  const getSelectedDeviceId = (): string => {
    return selectedMicrophoneId;
  };

  const refreshMicrophones = async (): Promise<void> => {
    await loadMicrophones();
  };

  return {
    availableMicrophones,
    selectedMicrophoneId,
    micSettingsAnchor,
    microphoneError,
    isLoadingMicrophones,
    handleMicSettingsClick,
    handleMicSettingsClose,
    handleMicrophoneSelect,
    getSelectedDeviceId,
    refreshMicrophones,
  };
};
